export const EARTH_RADIUS_KM = 6371.0088;

export interface GeoPoint {
  lat: number;
  lng: number;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** Haversine great-circle distance in kilometres. */
export function greatCircleDistanceKm(a: GeoPoint, b: GeoPoint): number {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dPhi = phi2 - phi1;
  const dLambda = toRad(b.lng - a.lng);
  const h =
    Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Point at fraction `f` (0..1) along the great circle from `a` to `b` (spherical slerp). */
export function interpolateGreatCircle(a: GeoPoint, b: GeoPoint, f: number): GeoPoint {
  const phi1 = toRad(a.lat);
  const lam1 = toRad(a.lng);
  const phi2 = toRad(b.lat);
  const lam2 = toRad(b.lng);
  const delta = greatCircleDistanceKm(a, b) / EARTH_RADIUS_KM;
  if (delta < 1e-9) {
    return { lat: a.lat, lng: a.lng };
  }
  const sinDelta = Math.sin(delta);
  const wa = Math.sin((1 - f) * delta) / sinDelta;
  const wb = Math.sin(f * delta) / sinDelta;
  const x = wa * Math.cos(phi1) * Math.cos(lam1) + wb * Math.cos(phi2) * Math.cos(lam2);
  const y = wa * Math.cos(phi1) * Math.sin(lam1) + wb * Math.cos(phi2) * Math.sin(lam2);
  const z = wa * Math.sin(phi1) + wb * Math.sin(phi2);
  return {
    lat: toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))),
    lng: toDeg(Math.atan2(y, x)),
  };
}

/** `segments + 1` evenly spaced points along the great circle, endpoints included. */
export function sampleGreatCircle(a: GeoPoint, b: GeoPoint, segments: number): GeoPoint[] {
  const points: GeoPoint[] = [];
  for (let i = 0; i <= segments; i += 1) {
    points.push(interpolateGreatCircle(a, b, i / segments));
  }
  return points;
}
